import React, { useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { clsx } from 'clsx';
import { Sidebar } from './Sidebar';
import { EnvBanner } from '../shared/EnvBanner';
import { OrganizationLoadingOverlay } from '../shared/OrganizationLoadingOverlay';
import { ProcessingIndicator } from '../shared/ProcessingIndicator';
import { TaskProgressIndicator } from '../shared/TaskProgressIndicator';
import { useGlobalKeyboard } from '../../hooks/useKeyboard';
import { useUIStore } from '../../stores/uiStore';
import { useOrganization } from '../../context/OrganizationContext';
import { useProcessingStore } from '../../stores/processingStore';
import { useTaskStore } from '../../stores/taskStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { useTaskDebug } from '../../hooks/useTaskDebug';
import { useEffect } from 'react';
import { PortalContainerContext } from '../../context/PortalContainerContext';

export const Layout: React.FC = () => {
  // Enable global keyboard shortcuts
  useGlobalKeyboard();

  // Enable task debugging
  useTaskDebug();

  const { sidebarCollapsed } = useUIStore();
  const { isLoading, currentOrganization } = useOrganization();
  const { activeProcess } = useProcessingStore();
  const { loadTasks } = useTaskStore();
  const { uiScale } = useSettingsStore();

  // Portal container ref for scaled dropdowns
  const portalContainerRef = useRef<HTMLDivElement>(null);
  const getPortalContainer = () => portalContainerRef.current || document.body;
  
  // Load tasks on mount
  useEffect(() => {
    loadTasks();
  }, [loadTasks]);
  
  // Apply UI scale to body
  useEffect(() => {
    if (uiScale !== 1.0) {
      document.body.classList.add('ui-scaled');
    } else {
      document.body.classList.remove('ui-scaled');
    }
  }, [uiScale]);
  
  // Determine scale class based on percentage
  const scalePercentage = Math.round(uiScale * 100);
  const scaleClass = `ui-scale-${scalePercentage}`;
  
  return (
    <PortalContainerContext.Provider value={{ portalContainerRef, getPortalContainer }}>
      <div className="ui-scale-container">
        <div className={clsx('ui-scale-wrapper', scaleClass)}>
          <EnvBanner />
          <div className="h-full bg-gray-50 flex">
            <Sidebar />

            {/* Processing header */}
            {activeProcess && (
              <div className={clsx(
                'fixed top-0 z-30 transition-all duration-300 border-b border-gray-200 bg-white shadow-sm',
                sidebarCollapsed ? 'lg:left-[70px]' : 'lg:left-64',
                'left-0 right-0 lg:right-0'
              )}>
                <div className="p-3">
                  <ProcessingIndicator />
                </div>
              </div>
            )}

            <main className={clsx(
              'transition-all duration-300 flex flex-col flex-1 h-full min-w-0 overflow-y-auto',
              'pt-16 lg:pt-0', // Add top padding on mobile for the menu button
              activeProcess && 'pt-20 lg:pt-16', // Add top padding for processing header
              sidebarCollapsed ? 'lg:pl-[70px]' : 'lg:pl-64',
              'pb-2' // Add bottom padding for task manager space (8px)
            )}>
              <div className="p-3 sm:p-4 lg:p-6 flex-1 flex flex-col min-w-0">
                <Outlet />
              </div>
            </main>

            <OrganizationLoadingOverlay
              isLoading={isLoading}
              organizationName={currentOrganization?.name}
            />

            {/* Dedicated space for task manager at bottom */}
            <div className={clsx(
              'fixed bottom-0 right-0 z-40',
              'transition-all duration-300',
              sidebarCollapsed ? 'lg:left-[70px]' : 'lg:left-64',
              'left-0'
            )}>
              <TaskProgressIndicator />
            </div>
          </div>

          {/* Portal container for dropdowns - inside scaled wrapper so they scale with UI */}
          <div
            ref={portalContainerRef}
            id="portal-container"
            className="portal-container"
          />
        </div>
      </div>
    </PortalContainerContext.Provider>
  );
};